import type { ReactNode } from "react";
import { SlideStatement } from "../../../SlideStatement";
import { SlideVerdict } from "../../../SlideVerdict";

// Roadmap (redesigned 2026-09-09, TPF): the previous version was seven
// identical rows, which read as a list rather than a timeline and gave equal
// weight to an internal milestone (the agentic factory — that belongs to
// slide 12 and appendix E, not here). This version is a left-to-right spine
// with four phases; each phase carries ONLY user-facing launches, and every
// phase closes on the revenue line those launches open. Launch names come
// from Linear's initiative/project tree, read 2026-09-09: Q3/Q4 2026 split the
// Apptonomy v3 initiative (Active) by project state, Q1 2027 is v4's LAUNCH
// projects, Beyond is v4's IMP projects. Shipped work was cut (TPF,
// 2026-09-09) — a roadmap looks forward; the track record lives elsewhere in
// the deck. It stops at ASO
// on purpose (TPF, 2026-09-09) — the adjacent verticals are slide 16's
// argument, and putting them here made the roadmap look unfocused.
type Phase = {
  when: string;
  state: "shipped" | "now" | "next" | "later";
  launches: ReactNode[];
  unlockLabel: string;
  unlock: ReactNode;
};

const PHASES: Phase[] = [
  {
    when: "Q3 2026",
    state: "now",
    launches: [
      <>
        <b>In-App Events &amp; Promotional Content</b> — recurring, scheduled
        store content, run for the customer
      </>,
      <>
        <b>Figma plugin</b> — creative straight from design into the store
        listing
      </>,
      <>
        <b>Pre-launch listing check</b> — metadata and creative graded before
        an app goes live
      </>,
    ],
    unlockLabel: "Unlocks",
    unlock: (
      <>
        The first <b>managed publishing programs</b> — audit findings become
        delivered work.
      </>
    ),
  },
  {
    when: "Q4 2026",
    state: "next",
    launches: [
      <>
        <b>Custom Product Pages / Custom Store Listings</b> — managed
        publishing on both stores
      </>,
      <>
        <b>ASO Localization</b> — listing text and screenshot text, every
        market we sell into
      </>,
      <>
        <b>Onboarding rethink</b> — one-screen start, actions-first results,
        portfolio import
      </>,
    ],
    unlockLabel: "Unlocks",
    unlock: (
      <>
        The bridge from one-off audit to <b>retained subscription</b> — work
        that recurs every month.
      </>
    ),
  },
  {
    when: "Q1 2027",
    state: "next",
    launches: [
      <>
        <b>Publishing</b> — recommendation straight to a live listing, closing
        the loop
      </>,
      <>
        <b>In-app ASO Strategist chat</b> — grounded in the customer's own
        listings, audits and results
      </>,
      <>
        <b>MCP &amp; API</b> · <b>Slack</b> — Apptonomy inside the tools the
        team already runs
      </>,
    ],
    unlockLabel: "Unlocks",
    unlock: (
      <>
        Into the agency stack, and the seat expansion that comes with{" "}
        <b>Elite and Enterprise</b>.
      </>
    ),
  },
  {
    when: "Beyond",
    state: "later",
    launches: [
      <>
        <b>Search-demand indicator</b> and <b>store-featuring readiness</b> —
        the two signals Apple never publishes
      </>,
      <>
        <b>AI vision creative analysis</b> — every competitor's screenshots
        read and scored against yours
      </>,
      <>
        <b>Anomaly detection</b> and a <b>weekly revision brief</b> — the
        platform raises the work before you ask
      </>,
    ],
    unlockLabel: "Unlocks",
    unlock: (
      <>
        ASO stops being something a team checks. The platform{" "}
        <b>brings the work to them</b>.
      </>
    ),
  },
];

export default function Roadmap() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="Roadmap">
        <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
          Every launch opens a revenue line.
        </h1>
      </SlideStatement>

      {/* Below the rule — the spine, then the four phases, then the close. */}
      <div className="flex flex-col gap-5 md:min-h-0 md:flex-1 md:justify-between md:gap-4 md:pt-6">
        <div className="flex flex-col gap-4 md:gap-2">
          <Spine />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4 md:gap-4">
            {PHASES.map((p) => (
              <PhaseCard key={p.when} {...p} />
            ))}
          </div>
        </div>

        <SlideVerdict compact>
          Two years of launches, and each one sells more to the same customer.
        </SlideVerdict>
      </div>
    </div>
  );
}

/* ---------- The time spine ----------
   A single rule with four markers on it, one per column, so the columns read
   as positions in time rather than as four cards that happen to sit in a row.
   The shipped marker is filled, the current one is filled and ringed, the two
   ahead are hollow. */
function Spine() {
  return (
    <div className="relative hidden h-4 md:block" aria-hidden="true">
      <div className="absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-slate-400 via-blue-500 to-slate-200" />
      <div className="relative grid h-full grid-cols-4">
        {PHASES.map((p) => (
          <div key={p.when} className="flex items-center justify-center">
            {p.state === "now" ? (
              <span className="h-4 w-4 rounded-full bg-blue-600 ring-4 ring-blue-200" />
            ) : p.state === "shipped" ? (
              <span className="h-3 w-3 rounded-full bg-slate-500" />
            ) : (
              <span className="h-3 w-3 rounded-full border-2 border-slate-400 bg-white" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PhaseCard({ when, state, launches, unlockLabel, unlock }: Phase) {
  const isNow = state === "now";
  const isShipped = state === "shipped";
  return (
    <div
      className={`flex flex-col rounded-xl p-4 md:p-3.5 ${
        isNow
          ? "bg-blue-50 ring-2 ring-blue-400"
          : isShipped
            ? "bg-slate-50 ring-1 ring-slate-200"
            : "bg-white ring-1 ring-slate-200"
      }`}
    >
      {/* The quarter IS the heading (TPF, 2026-09-09) — the phase nicknames
          above it were a second title saying less than the date. */}
      <h3
        className={`text-xl font-bold leading-tight tracking-tight md:text-2xl ${
          isNow ? "text-blue-700" : "text-slate-900"
        }`}
      >
        {when}
      </h3>

      <ul className="mt-2.5 flex flex-col gap-1.5 text-sm leading-snug text-slate-600 md:mt-2.5 md:gap-1.5 md:text-[15px] [&_b]:font-semibold [&_b]:text-slate-900">
        {launches.map((l, i) => (
          <li key={i} className="flex gap-2">
            <span
              aria-hidden="true"
              className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${
                isNow ? "bg-blue-500" : isShipped ? "bg-slate-400" : "bg-slate-300"
              }`}
            />
            <span>{l}</span>
          </li>
        ))}
      </ul>

      {/* The revenue line the launches above open — same slot in every column,
          so the reader can scan the unlocks across the timeline on their own. */}
      <div
        className={`mt-auto border-t pt-2.5 md:pt-2.5 ${
          isNow ? "border-blue-300" : "border-slate-200"
        }`}
      >
        <p
          className={`text-[11px] font-bold uppercase tracking-[0.14em] md:text-xs ${
            isNow ? "text-blue-700" : "text-slate-500"
          }`}
        >
          {unlockLabel}
        </p>
        <p
          className={`mt-1 text-sm leading-snug md:text-[15px] [&_b]:font-semibold ${
            isNow
              ? "text-blue-900 [&_b]:text-blue-900"
              : "text-slate-700 [&_b]:text-slate-900"
          }`}
        >
          {unlock}
        </p>
      </div>
    </div>
  );
}
